import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { _t } from '@/i18n/t';
import { cn } from '@/shared/lib/utils';
import '@/styles/design-system.css';
import './scan.css';
import Topbar from '../components/Topbar';
import AmbientGlow from '../components/AmbientGlow';
import SpinnerDot from '../components/SpinnerDot';
import { type Chapter } from '../components/ChapterRow';
import ScanStageRail from '../components/ScanStageRail';
import ScanFocalChapter from '../components/ScanFocalChapter';
import ScanEscape from '../components/ScanEscape';
import ScanReportCard from '../components/ScanReportCard';
import SignalsOdometer from '../components/SignalsOdometer';
import DuplicateWorkspacePanel from '../components/DuplicateWorkspacePanel';
import { useExistingCompany } from '../hooks/useExistingCompany';
import { useScanStream, requestScanResume, type ScanFailReason } from '../hooks/useScanStream';
import type { SiteScanData } from '../components/SiteScanPanel';
import { normalizeAssistantProvider, type AssistantProvider } from '../utils/assistantProviders';
import { hostOf, type NameSource } from '../utils/siteIdentity';
import { toPersonaCard } from '../utils/personaCards';
import type { ScanResult } from '../types';
import type { AudienceFact, SiteScanStepEvent } from '../hooks/scanEventRouter';
import type { CustomerPersonaCard } from '../components/CustomersPanel';
import { audienceFill, siteFill } from '../utils/scanProgress';

const SITE_TICK_MS = 250;

const EXPECTED_PERSONA_COUNT = 4;
const STEP_FINISH_DWELL_MS = 2000;
const MESSAGE_REPLAY_INTERVAL_MS = 650;
const MESSAGE_REPLAY_MAX = 6;
const PAGE_REVEAL_DWELL_MS = 300;
const PERSONA_REVIEW_INTERVAL_MS = 1200;
// The run gives no signal that the last persona has been written, so the pass
// infers it from silence. The last one can arrive in a final burst well over a
// minute after the one before it, so this sits clear of that, not of the gaps
// between the early ones.
const PERSONA_QUIET_MS = 180_000;
const BACKGROUND_BUTTON_MIN_PCT = 30;
const PRESENTATION_STORAGE_PREFIX = 'onboarding.scanPresentation.';
// The report card is the scan's last beat, not a flash between two screens.
const REPORT_HOLD_MS = 1800;
// A pass that is stuck may not hold the screen for ever. This bounds one pass,
// not the whole tail: the run typically ends while the first pass is still on
// screen, so a single timer from that moment would cut the later passes off.
const PRESENTATION_PASS_MAX_MS = 45_000;

interface ScanPresentationCheckpoint {
  chapterIndex: number;
  revealedPageCount: number;
  revealedPersonaCount: number;
}

function readPresentationCheckpoint(scanId: string | null | undefined, chapterCount: number): ScanPresentationCheckpoint | null {
  if (!scanId) return null;
  try {
    const raw = sessionStorage.getItem(`${PRESENTATION_STORAGE_PREFIX}${scanId}`);
    if (!raw) return null;
    const saved = JSON.parse(raw) as Partial<ScanPresentationCheckpoint>;
    return {
      chapterIndex: Math.max(0, Math.min(chapterCount - 1, Number.isFinite(saved.chapterIndex) ? saved.chapterIndex! : 0)),
      revealedPageCount: Math.max(0, Number.isFinite(saved.revealedPageCount) ? saved.revealedPageCount! : 0),
      revealedPersonaCount: Math.max(1, Number.isFinite(saved.revealedPersonaCount) ? saved.revealedPersonaCount! : 1),
    };
  } catch {
    return null;
  }
}

// Four passes, and only four. Visuals and trust signals moved to the build,
// and compiling is no longer a stage — it is the report card the scan ends on.
// Their agents still run, and their events still count toward progress and the
// signals tally; they simply have no stage and no panel here.
const ALL_CHAPTERS: Chapter[] = [
  { key: 'site', label: _t('onboarding.crawl.chapter.site.label'), settled: _t('onboarding.crawl.chapter.site.settled'), details: [], stageName: _t('onboarding.crawl.chapter.site.stageName') },
  { key: 'customers', label: _t('onboarding.crawl.chapter.customers.label'), settled: _t('onboarding.crawl.chapter.customers.settled'), details: [], stageName: _t('onboarding.crawl.chapter.customers.stageName') },
  { key: 'google', label: _t('onboarding.crawl.chapter.google.label'), settled: _t('onboarding.crawl.chapter.google.settled'), details: [], stageName: _t('onboarding.crawl.chapter.google.stageName') },
  { key: 'assistants', label: _t('onboarding.crawl.chapter.assistants.label'), settled: _t('onboarding.crawl.chapter.assistants.settled'), details: [], stageName: _t('onboarding.crawl.chapter.assistants.stageName') },
];

const RESCAN_CHAPTER_KEYS = ['site', 'google', 'assistants'];

// These passes own an animated panel that is a better replay than their log
// lines, so they show the panel and hold the stage until it has played out.
const SELF_PLAYING_CHAPTER_KEYS = ['google', 'assistants'];

const AGENT_CHAPTER_KEY: Record<string, string> = {
  'scan-planner':            'site',
  'pre-fetch':               'site',
  'site-page-fetcher':       'site',
  'social-proof-fetcher':    'trust',
  'image-curator':           'visuals',
  'brand-synthesizer':       'customers',
  'google-citation-checker': 'google',
  'ai-citation-checker':     'assistants',
  'content-strategist':      'compiling',
  'content-lead':            'compiling',
};

const NON_RESCAN_AGENTS = new Set(['image-curator', 'brand-synthesizer', 'content-strategist', 'content-lead']);

// A rescan spends its last couple of minutes on these, after every agent with a
// chapter has finished. Counting them keeps its bar moving instead of holding at 90.
const RESCAN_FACT_AGENTS = ['fact-page-gate', 'fact-extractor', 'fact-key-canonicalizer', 'fact-reconciler', 'fact-auditor'];

export function estimatePctFromCompletedAgents(completedAgents: string[], rescan?: boolean): number {
  const relevantAgents = rescan ? progressAgents(true) : Object.keys(AGENT_CHAPTER_KEY);
  if (relevantAgents.length === 0) return 0;
  const done = relevantAgents.filter(a => completedAgents.includes(a)).length;
  return Math.min(90, Math.round((done / relevantAgents.length) * 90));
}

const POST_HANDOFF_AGENTS = new Set(['content-strategist']);

function progressAgents(rescan?: boolean): string[] {
  const chapterAgents = Object.keys(AGENT_CHAPTER_KEY).filter(a =>
    (rescan ? !NON_RESCAN_AGENTS.has(a) : !POST_HANDOFF_AGENTS.has(a)));
  return rescan ? [...chapterAgents, ...RESCAN_FACT_AGENTS] : chapterAgents;
}

export function progressBoundsFor(completedAgents: Set<string>, rescan?: boolean): { floor: number; ceiling: number } {
  const relevant = progressAgents(rescan);
  if (relevant.length === 0) return { floor: 0, ceiling: 90 };
  const share = 90 / relevant.length;
  const done = relevant.filter(a => completedAgents.has(a)).length;
  return {
    floor: Math.round(share * done),
    ceiling: Math.round(Math.min(90, share * (done + 1))),
  };
}

function chapterPeerAgents(chapterKey: string, rescan?: boolean): string[] {
  return Object.entries(AGENT_CHAPTER_KEY)
    .filter(([agent, key]) => key === chapterKey && (!rescan || !NON_RESCAN_AGENTS.has(agent)))
    .map(([agent]) => agent);
}

function settledChaptersFromCompletedAgents(
  chapters: Chapter[],
  completedAgents: string[],
  rescan?: boolean,
): Record<string, 'settled'> {
  const settled: Record<string, 'settled'> = {};
  for (const c of chapters) {
    const peers = chapterPeerAgents(c.key, rescan);
    if (peers.length > 0 && peers.every(a => completedAgents.includes(a))) {
      settled[c.key] = 'settled';
    }
  }
  return settled;
}

function firstUnsettledChapterIndex(chapters: Chapter[], completedAgents: string[], rescan?: boolean): number {
  const settled = settledChaptersFromCompletedAgents(chapters, completedAgents, rescan);
  const index = chapters.findIndex(c => !settled[c.key]);
  return index === -1 ? chapters.length - 1 : index;
}

function settledChaptersBefore(
  chapters: Chapter[],
  settled: Record<string, 'settled'>,
  index: number,
): Record<string, 'settled'> {
  return Object.fromEntries(chapters.slice(0, index).filter(c => settled[c.key]).map(c => [c.key, 'settled' as const]));
}

// Rescan reuses this screen but is not part of the onboarding wizard, so it
// keeps its own wording rather than the prototype's.
function rescanCancelCopy(hasPrevious?: boolean) {
  const base = hasPrevious ? 'rescan.cancel' : 'rescan.cancelFirst';
  return {
    link: _t(`${base}.button`),
    note: _t(`${base}.confirmDesc`),
    confirm: _t(`${base}.confirmLabel`),
    keep: _t(`${base}.dismissLabel`),
  };
}

function initialMessagesByChapter(agentMessages?: Record<string, string[]>): Record<string, string[]> {
  if (!agentMessages) return {};
  const messages: Record<string, string[]> = {};
  for (const [agent, values] of Object.entries(agentMessages)) {
    const chapterKey = AGENT_CHAPTER_KEY[agent];
    if (!chapterKey) continue;
    messages[chapterKey] = [...(messages[chapterKey] ?? []), ...values].slice(-MESSAGE_REPLAY_MAX);
  }
  return messages;
}


type FallbackReason = Exclude<ScanFailReason, 'duplicate'>;

const FALLBACK_COPY: Record<FallbackReason, { kicker: string; head: string; sub: string | null; eta?: string; canRetry: boolean }> = {
  stalled: {
    kicker: _t('onboarding.crawl.fallback.stalledKicker'),
    head: _t('onboarding.crawl.fallback.stalledHead'),
    sub: _t('onboarding.crawl.fallback.stalledSub'),
    eta: _t('onboarding.crawl.fallback.stalledEta'),
    canRetry: true,
  },
  lost: {
    kicker: _t('onboarding.crawl.fallback.stalledKicker'),
    head: _t('onboarding.crawl.fallback.stalledHead'),
    sub: _t('onboarding.crawl.fallback.stalledSub'),
    eta: _t('onboarding.crawl.fallback.stalledEta'),
    canRetry: true,
  },
  generic: {
    kicker: _t('onboarding.crawl.fallback.errorKicker'),
    head: _t('onboarding.crawl.fallback.errorHead'),
    sub: _t('onboarding.crawl.fallback.stalledSub'),
    canRetry: true,
  },
};

const DISCOVERY_LOG_MAX = 6;

function applySiteScanStep(prev: SiteScanData, ev: SiteScanStepEvent): SiteScanData {
  switch (ev.kind) {
    case 'discovery': {
      if (prev.discoveryLog[prev.discoveryLog.length - 1] === ev.message) return prev;
      return { ...prev, discoveryLog: [...prev.discoveryLog, ev.message].slice(-DISCOVERY_LOG_MAX) };
    }
    case 'pages-list':
      return { ...prev, totalPages: ev.total };
    case 'page-fetching':
      return prev;
    case 'page-done':
      if (!ev.title) return prev;
      return {
        ...prev,
        pages: [...prev.pages.filter(p => p.url !== ev.url), { url: ev.url, title: ev.title }],
        imageCount: prev.imageCount + (ev.images ?? 0),
      };
    case 'preview-image':
      return prev.firstImageUrl ? prev : { ...prev, firstImageUrl: ev.firstImageUrl };
    case 'images-annotating':
      return {
        ...prev,
        totalImages: ev.count,
        firstImageUrl: ev.firstImageUrl ?? prev.firstImageUrl,
        imageUrls: ev.imageUrls.length > 0 ? ev.imageUrls : prev.imageUrls,
      };
    case 'images-done':
      return { ...prev, totalImages: ev.curated, imagesDone: true };
    default:
      return prev;
  }
}

interface CrawlStatusProps {
  name: string;
  nameSource?: NameSource;
  url: string;
  businessType?: string;
  description?: string;
  onDone: () => void;
  onRunInBackground?: (pct: number) => void;
  onCancel?: () => void;
  onStartOver?: () => void;
  onOpenExistingWorkspace?: (companyId: number | null) => void;
  onError?: () => void;
  minimized?: boolean;
  initialPct?: number;
  onPctChange?: (pct: number) => void;
  initialCompletedAgents?: string[];
  initialAgentMessages?: Record<string, string[]>;
  initialPersonas?: CustomerPersonaCard[];
  scanId?: string | null;
  onScanCreated?: (id: string) => void;
  onCompanyCreated?: (companyId: number) => void;
  onScanComplete?: (result: ScanResult) => void | Promise<void>;
  onBrandNameDetected?: (brandName: string) => void;
  rescan?: boolean;
  hasPrevious?: boolean;
}

export default function CrawlStatus({
  name, nameSource, url, businessType, description, onDone, onRunInBackground, onCancel, onStartOver, onOpenExistingWorkspace, onError, minimized, onPctChange,
  scanId: initialScanId, onScanCreated, onCompanyCreated, onScanComplete, onBrandNameDetected, rescan, hasPrevious,
  initialPct, initialCompletedAgents, initialAgentMessages, initialPersonas,
}: CrawlStatusProps) {
  const CHAPTERS = useMemo(
    () => (rescan ? ALL_CHAPTERS.filter(c => RESCAN_CHAPTER_KEYS.includes(c.key)) : ALL_CHAPTERS),
    [rescan],
  );

  const applyTerminalPersonas = (result: ScanResult) => {
    const resultPersonas = result.contentStrategy?.personas?.map((persona, index) => toPersonaCard(persona, index)) ?? [];
    if (resultPersonas.length > 0) setPersonas(resultPersonas);
  };
  const initialPresentation = useRef(
    readPresentationCheckpoint(initialScanId, CHAPTERS.length) ?? {
      chapterIndex: firstUnsettledChapterIndex(CHAPTERS, initialCompletedAgents ?? [], rescan),
      revealedPageCount: 0,
      revealedPersonaCount: 1,
    },
  ).current;
  const initiallySettled = settledChaptersBefore(
    CHAPTERS,
    settledChaptersFromCompletedAgents(CHAPTERS, initialCompletedAgents ?? [], rescan),
    initialPresentation.chapterIndex,
  );
  const [displayName, setDisplayName] = useState(name);
  const [brandConfirmed, setBrandConfirmed] = useState(!!rescan);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState<ScanFailReason | null>(null);
  const [existingCompany, setExistingCompany] = useState('');
  const [existingCompanyId, setExistingCompanyId] = useState<number | null>(null);
  const [signals, setSignals] = useState(0);
  const [resumeKey, setResumeKey] = useState(0);
  const [resuming, setResuming] = useState(false);

  const [slowChapters, setSlowChapters] = useState<Set<string>>(new Set());
  const scanIdForResumeRef = useRef<string | null>(initialScanId ?? null);
  const [scanReady, setScanReady] = useState(!!initialScanId);
  const resumedPct = initialPct ?? (initialCompletedAgents ? estimatePctFromCompletedAgents(initialCompletedAgents, rescan) : 0);
  const resumedBounds = progressBoundsFor(new Set(initialCompletedAgents ?? []), rescan);
  const [pct, setPct] = useState(resumedPct);
  const [chapterStatus, setChapterStatus] = useState<Record<string, 'live' | 'settled'>>(
    () => initiallySettled,
  );
  const [chapterMessages, setChapterMessages] = useState<Record<string, string[]>>(
    () => initialMessagesByChapter(initialAgentMessages),
  );
  const [siteScanData, setSiteScanData] = useState<SiteScanData>({
    pages: [], totalPages: 0, imageCount: 0, totalImages: 0, firstImageUrl: null, imageUrls: [], imagesDone: false, discoveryLog: [],
  });
  const [googleQueries, setGoogleQueries] = useState<string[]>([]);
  const [assistantQueries, setAssistantQueries] = useState<string[]>([]);
  const [assistantActiveProvider, setAssistantActiveProvider] = useState<AssistantProvider | undefined>();
  const [assistantProviderCounts, setAssistantProviderCounts] = useState<Partial<Record<AssistantProvider, number>>>({});
  const [assistantProviderResults, setAssistantProviderResults] = useState<Record<string, AssistantProvider[]>>({});
  const [personas, setPersonas] = useState<CustomerPersonaCard[]>(() => initialPersonas ?? []);
  const [audienceFacts, setAudienceFacts] = useState<AudienceFact[]>([]);
  const [personaAgentLines, setPersonaAgentLines] = useState<string[]>([]);
  const personasRef = useRef<CustomerPersonaCard[]>(initialPersonas ?? []);
  const [revealedPageCount, setRevealedPageCount] = useState(initialPresentation.revealedPageCount);
  const [chapterElapsedMs, setChapterElapsedMs] = useState(0);
  const chapterStartedAt = useRef<{ key: string; at: number } | null>(null);
  const lastCardElapsedRef = useRef({ drawn: 0, at: 0 });
  const revealedPageCountRef = useRef(initialPresentation.revealedPageCount);
  const [revealedPersonaCount, setRevealedPersonaCount] = useState(initialPresentation.revealedPersonaCount);
  const revealedPersonaCountRef = useRef(initialPresentation.revealedPersonaCount);
  const [personasQuiet, setPersonasQuiet] = useState(false);
  const pagesListReceivedRef = useRef(false);
  const totalPagesRef = useRef(0);
  const pagesArrivedCountRef = useRef(0);
  const chapterStatusRef = useRef<Record<string, 'live' | 'settled'>>(initiallySettled);
  const completedAgentsRef = useRef<Set<string>>(new Set(initialCompletedAgents ?? []));
  const dwellTimersRef = useRef<number[]>([]);
  const chapterMessagesRef = useRef<Record<string, string[]>>(initialMessagesByChapter(initialAgentMessages));
  const pctRef = useRef(resumedPct);
  const [presentedChapterIndex, setPresentedChapterIndex] = useState(initialPresentation.chapterIndex);
  const presentedChapterIndexRef = useRef(initialPresentation.chapterIndex);
  const [replayMessages, setReplayMessages] = useState<string[] | null>(null);
  const [dwellChapterKey, setDwellChapterKey] = useState<string | null>(null);
  const [playbackDoneChapters, setPlaybackDoneChapters] = useState<Record<string, true>>({});
  const playbackDoneRef = useRef<Set<string>>(new Set());
  const presentationStartedRef = useRef<Set<string>>(new Set());
  const chapterSeenRunningRef = useRef<Set<string>>(new Set());
  const [backendDone, setBackendDone] = useState(false);
  const backendDoneRef = useRef(false);
  const presentationCompleteRef = useRef(false);
  const finishedRef = useRef(false);
  // Rescan's onScanComplete kicks off a server-side site health audit; hold the
  // "scan done" signal until that settles so the UI doesn't close mid-audit.
  const pendingCompletionRef = useRef<Promise<void> | null>(null);
  const focalChapter = CHAPTERS[presentedChapterIndex] ?? CHAPTERS[CHAPTERS.length - 1];

  useEffect(() => {
    const key = focalChapter.key;
    if (key !== 'site' && key !== 'customers') return;
    if (chapterStartedAt.current?.key !== key) {
      chapterStartedAt.current = { key, at: Date.now() };
      setChapterElapsedMs(0);
    }
    const id = window.setInterval(() => {
      setChapterElapsedMs(Date.now() - (chapterStartedAt.current?.at ?? Date.now()));
    }, SITE_TICK_MS);
    return () => window.clearInterval(id);
  }, [focalChapter.key]);

  const persistPresentationCheckpoint = useCallback(() => {
    const sid = scanIdForResumeRef.current ?? initialScanId;
    if (!sid || minimized) return;
    try {
      sessionStorage.setItem(`${PRESENTATION_STORAGE_PREFIX}${sid}`, JSON.stringify({
        chapterIndex: presentedChapterIndexRef.current,
        revealedPageCount: revealedPageCountRef.current,
        revealedPersonaCount: revealedPersonaCountRef.current,
      } satisfies ScanPresentationCheckpoint));
    } catch {
      // The checkpoint is an enhancement; reconnecting to the scan still works.
    }
  }, [initialScanId, minimized]);

  const clearPresentationCheckpoint = () => {
    const sid = scanIdForResumeRef.current ?? initialScanId;
    if (!sid) return;
    try {
      sessionStorage.removeItem(`${PRESENTATION_STORAGE_PREFIX}${sid}`);
    } catch {
      // Ignore unavailable storage.
    }
  };

  useEffect(() => () => { dwellTimersRef.current.forEach(clearTimeout); }, []);

  useEffect(() => {
    persistPresentationCheckpoint();
  }, [persistPresentationCheckpoint, presentedChapterIndex, revealedPageCount, revealedPersonaCount]);

  useEffect(() => { personasRef.current = personas; }, [personas]);

  const markChapter = (key: string | undefined, next: 'live' | 'settled') => {
    if (!key || !CHAPTERS.some(c => c.key === key)) return;
    const current = chapterStatusRef.current[key];
    if (current === 'settled') return;
    if (current === next) return;
    chapterStatusRef.current = { ...chapterStatusRef.current, [key]: next };
    setChapterStatus(chapterStatusRef.current);
  };

  const markPlaybackDone = (chapterKey: string) => {
    if (playbackDoneRef.current.has(chapterKey)) return;
    playbackDoneRef.current.add(chapterKey);
    setPlaybackDoneChapters(prev => ({ ...prev, [chapterKey]: true }));
  };

  const settleChapterNow = (chapterKey: string) => {
    markChapter(chapterKey, 'settled');
    setSlowChapters(prev => {
      if (!prev.has(chapterKey)) return prev;
      const next = new Set(prev);
      next.delete(chapterKey);
      return next;
    });
  };

  // Capture every SSE message even when its agent is running off-screen. Later
  // chapters consume this buffer as a local UI replay; no agent is re-triggered.
  const applyAgentMessage = (agentType: string, message: string) => {
    const chapterKey = AGENT_CHAPTER_KEY[agentType];
    if (!chapterKey) return;
    markChapter(chapterKey, 'live');

    const existing = chapterMessagesRef.current[chapterKey] ?? [];
    const isHeartbeat = message.startsWith('Still ');
    const lastIsHeartbeat = existing.length > 0 && existing[existing.length - 1]?.startsWith('Still ');
    const base = isHeartbeat && lastIsHeartbeat ? existing.slice(0, -1) : existing;
    const next = [...base.slice(-(MESSAGE_REPLAY_MAX - 1)), message];
    chapterMessagesRef.current = { ...chapterMessagesRef.current, [chapterKey]: next };
    setChapterMessages(chapterMessagesRef.current);
  };

  const trySettleChapter = (chapterKey: string) => {
    const peers = chapterPeerAgents(chapterKey, rescan);
    if (peers.length > 0 && peers.every(a => completedAgentsRef.current.has(a))) settleChapterNow(chapterKey);
  };

  useEffect(() => {
    const total = siteScanData.pages.length;
    if (revealedPageCount >= total) return;
    const delay = revealedPageCount === 0 ? 0 : PAGE_REVEAL_DWELL_MS;
    const timer = window.setTimeout(() => {
      revealedPageCountRef.current = revealedPageCount + 1;
      setRevealedPageCount(revealedPageCountRef.current);
    }, delay);
    dwellTimersRef.current.push(timer);
    return () => clearTimeout(timer);
  }, [siteScanData.pages.length, revealedPageCount]);

  useEffect(() => {
    if (minimized || focalChapter?.key !== 'customers') return;
    const total = Math.min(EXPECTED_PERSONA_COUNT, personas.length);
    if (revealedPersonaCount >= total) return;
    const timer = window.setTimeout(() => {
      revealedPersonaCountRef.current = revealedPersonaCount + 1;
      setRevealedPersonaCount(revealedPersonaCountRef.current);
    }, PERSONA_REVIEW_INTERVAL_MS);
    dwellTimersRef.current.push(timer);
    return () => clearTimeout(timer);
  }, [focalChapter?.key, minimized, personas.length, revealedPersonaCount]);

  useEffect(() => {
    if (personas.length === 0) return;
    setPersonasQuiet(false);
    const timer = window.setTimeout(() => setPersonasQuiet(true), PERSONA_QUIET_MS);
    dwellTimersRef.current.push(timer);
    return () => clearTimeout(timer);
  }, [personas.length]);

  const finishOnboarding = () => {
    if (!backendDoneRef.current || !presentationCompleteRef.current || finishedRef.current) return;
    finishedRef.current = true;
    clearPresentationCheckpoint();
    setDone(true);
    if (minimized) {
      onDone();
      return;
    }
    // The report card is the last thing the scan says. Hold it long enough to
    // be read before the reveal takes over.
    dwellTimersRef.current.push(window.setTimeout(onDone, REPORT_HOLD_MS));
  };

  const advancePresentedChapter = (chapterKey: string) => {
    const current = CHAPTERS[presentedChapterIndexRef.current];
    if (!current || current.key !== chapterKey) return;
    setReplayMessages(null);
    if (presentedChapterIndexRef.current >= CHAPTERS.length - 1) {
      presentationCompleteRef.current = true;
      finishOnboarding();
      return;
    }
    setDwellChapterKey(null);
    const nextIndex = presentedChapterIndexRef.current + 1;
    presentedChapterIndexRef.current = nextIndex;
    setPresentedChapterIndex(nextIndex);
  };

  const finishPresentedChapter = (chapterKey: string, replay: boolean) => {
    if (presentationStartedRef.current.has(chapterKey)) return;
    presentationStartedRef.current.add(chapterKey);
    const buffered = replay ? (chapterMessagesRef.current[chapterKey] ?? []).slice(-MESSAGE_REPLAY_MAX) : [];

    const beginDwell = () => {
      setReplayMessages(null);
      setDwellChapterKey(chapterKey);
      const dwellTimer = window.setTimeout(() => advancePresentedChapter(chapterKey), STEP_FINISH_DWELL_MS);
      dwellTimersRef.current.push(dwellTimer);
    };

    if (buffered.length === 0) {
      beginDwell();
      return;
    }

    setReplayMessages([]);
    buffered.forEach((message, index) => {
      const timer = window.setTimeout(() => {
        setReplayMessages(previous => [...(previous ?? []), message]);
        if (index === buffered.length - 1) {
          const revealTimer = window.setTimeout(beginDwell, MESSAGE_REPLAY_INTERVAL_MS);
          dwellTimersRef.current.push(revealTimer);
        }
      }, MESSAGE_REPLAY_INTERVAL_MS * index);
      dwellTimersRef.current.push(timer);
    });
  };

  // The audience pass owns agents that finish long before its personas exist, so
  // agent completion alone is not enough to leave. Wait for the cards, unless the
  // run is already over and none are coming.
  const chapterContentFullyShown = (chapterKey: string) => {
    if (SELF_PLAYING_CHAPTER_KEYS.includes(chapterKey)) return playbackDoneRef.current.has(chapterKey);
    if (chapterKey === 'site') return siteScanData.pages.length === 0 || playbackDoneRef.current.has('site');
    if (chapterKey !== 'customers') return true;
    if (personas.length === 0) return backendDoneRef.current;
    // Whatever has arrived gets shown before the pass hands over, even once the
    // run itself is finished — the last card lands late and would be skipped.
    if (revealedPersonaCount < Math.min(personas.length, EXPECTED_PERSONA_COUNT)) return false;
    if (personas.length >= EXPECTED_PERSONA_COUNT) return true;
    // Short of the full set: wait for the run to end rather than for a gap in
    // the stream, since a gap is not evidence that no more are coming.
    return backendDoneRef.current || personasQuiet;
  };

  // Presentation is deliberately sequential even though the agents remain parallel.
  // A chapter that completed off-screen replays its captured SSE messages, then every
  // chapter holds its completed result for exactly two seconds before advancing.
  const focalChapterKey = focalChapter.key;
  const focalChapterStatus = chapterStatus[focalChapterKey];
  const focalPlaybackDone = playbackDoneChapters[focalChapterKey];
  const presentationStepsRef = useRef({ chapterContentFullyShown, finishPresentedChapter, advancePresentedChapter });
  useEffect(() => {
    presentationStepsRef.current = { chapterContentFullyShown, finishPresentedChapter, advancePresentedChapter };
  });

  useEffect(() => {
    if (minimized || !focalChapter) return;
    const steps = presentationStepsRef.current;
    if (focalChapterStatus !== 'settled') {
      chapterSeenRunningRef.current.add(focalChapterKey);
      return;
    }
    if (!steps.chapterContentFullyShown(focalChapterKey)) return;
    steps.finishPresentedChapter(
      focalChapterKey,
      focalChapterKey !== 'customers'
        && !SELF_PLAYING_CHAPTER_KEYS.includes(focalChapterKey)
        && !chapterSeenRunningRef.current.has(focalChapterKey),
    );
  }, [minimized, focalChapter, focalChapterKey, focalChapterStatus, backendDone, personas.length, revealedPersonaCount, personasQuiet, siteScanData.pages.length, focalPlaybackDone]);

  const { setProgressBounds } = useScanStream({
    url,
    name,
    nameSource,
    businessType,
    initialScanId: scanIdForResumeRef.current ?? initialScanId,
    initialPct: resumedPct,
    initialProgressFloor: resumedBounds.floor,
    initialProgressCeiling: resumedBounds.ceiling,
    resumeKey,
    handOffOnAnalysisReady: !rescan,
    // analysis-ready arrives before the first persona exists. Keep the stream
    // open until the audience pass has actually shown its cards.
    readyToHandOff: () =>
      personasRef.current.length >= EXPECTED_PERSONA_COUNT
      && revealedPersonaCountRef.current >= EXPECTED_PERSONA_COUNT,
    // Each persona that lands restarts the hold, so a run still writing them
    // keeps its stream rather than losing the last one to the cap.
    holdProgress: () => personasRef.current.length,
    onAnalysisReady: (result) => {
      applyTerminalPersonas(result);
      onScanComplete?.(result);
    },
    onPctChange: (next) => { pctRef.current = next; setPct(next); onPctChange?.(next); },
    onSignalsChange: setSignals,
    onScanCreated: (id) => {
      scanIdForResumeRef.current = id;
      persistPresentationCheckpoint();
      setScanReady(true);
      onScanCreated?.(id);
    },
    onCompanyCreated: (id) => onCompanyCreated?.(id),
    onScanComplete: (result) => {
      // The terminal payload is authoritative and closes any gap caused by a
      // missed/replayed persona-ready event before the presentation queue settles.
      applyTerminalPersonas(result);
      const outcome = onScanComplete?.(result);
      if (rescan && outcome && typeof (outcome as Promise<void>).then === 'function') {
        pendingCompletionRef.current = outcome as Promise<void>;
      }
    },
    onBrandNameDetected: (bn) => { setDisplayName(bn); setBrandConfirmed(true); onBrandNameDetected?.(bn); },
    onDone: () => {
      const markDone = () => {
        backendDoneRef.current = true;
        setBackendDone(true);
        if (minimized) {
          presentationCompleteRef.current = true;
          finishOnboarding();
          return;
        }
        // A terminal scan event means every agent is finished even if a lifecycle
        // event was missed during reconnect. Unblock the presentation queue only.
        const settled = Object.fromEntries(CHAPTERS.map(chapter => [chapter.key, 'settled' as const]));
        chapterStatusRef.current = settled;
        setChapterStatus(settled);
        // The remaining passes play out and the last one finishes the screen
        // itself; the per-pass backstop below covers a pass that gets stuck.
      };
      // For rescan, the site health audit runs after the scan itself completes —
      // wait for it so the UI doesn't close while it's still auditing the site.
      if (pendingCompletionRef.current) {
        pendingCompletionRef.current.then(markDone, markDone);
      } else {
        markDone();
      }
    },
    onError: (reason, detail) => {
      setFailed(reason);
      setExistingCompany(detail?.existingCompany ?? '');
      setExistingCompanyId(detail?.existingCompanyId ?? null);
    },
    onSiteScanStep: (ev) => {
      if (ev.kind === 'images-curated') return;
      const chapterForKind: Partial<Record<typeof ev.kind, string>> = {
        'pages-list': 'site', 'page-fetching': 'site', 'page-done': 'site',
      };
      const targetKey = chapterForKind[ev.kind];
      if (targetKey !== undefined) markChapter(targetKey, 'live');
      if (ev.kind === 'pages-list') pagesListReceivedRef.current = true;
      setSiteScanData(prev => {
        const next = applySiteScanStep(prev, ev);
        totalPagesRef.current = next.totalPages;
        pagesArrivedCountRef.current = next.pages.length;
        return next;
      });
      if (ev.kind === 'pages-list' || ev.kind === 'page-done') trySettleChapter('site');
    },
    onAudienceFact: (fact) => {
      markChapter('customers', 'live');
      setAudienceFacts(prev => [...prev.filter(f => f.field !== fact.field), fact]);
    },
    onAgentStep: (agentType, label, message, detail) => {
      if (agentType === 'content-strategist' && label === 'synthesizing' && message) {
        setPersonaAgentLines(prev => [...prev.slice(-(MESSAGE_REPLAY_MAX - 1)), message]);
      }
      if (agentType === 'content-strategist' && label === 'persona-ready' && detail) {
        const persona = detail.persona as Record<string, unknown> | undefined;
        const index = detail.index as number | undefined;
        if (persona && typeof index === 'number') {
          markChapter('customers', 'live');
          setPersonas(prev => {
            const card = toPersonaCard(persona, index);
            const existingIdx = prev.findIndex(p => p.index === index);
            const next = existingIdx >= 0
              ? prev.map((p, i) => i === existingIdx ? card : p)
              : [...prev, card].sort((a, b) => a.index - b.index);
            return next;
          });
        }
      }
      if (agentType === 'google-citation-checker' && label === 'analyzing' && detail?.phase === 'check-query') {
        if (typeof detail.query === 'string') {
          const q = detail.query;
          setGoogleQueries(prev => prev.includes(q) ? prev : [...prev, q]);
        }
      }
      if (agentType === 'ai-citation-checker' && label === 'analyzing' && detail?.phase === 'check-query') {
        if (typeof detail.query === 'string') {
          const q = detail.query;
          setAssistantQueries(prev => prev.includes(q) ? prev : [...prev, q]);
        }
        const provider = normalizeAssistantProvider(typeof detail?.provider === 'string' ? detail.provider : undefined);
        if (provider) {
          setAssistantActiveProvider(provider);
          setAssistantProviderCounts(prev => ({ ...prev, [provider]: (prev[provider] ?? 0) + 1 }));
        }
      }
      if (agentType === 'ai-citation-checker' && label === 'analyzing' && detail?.phase === 'provider-result') {
        const provider = normalizeAssistantProvider(typeof detail?.provider === 'string' ? detail.provider : undefined);
        const query = typeof detail?.query === 'string' ? detail.query : '';
        if (provider && query) {
          setAssistantProviderResults(prev => {
            const settled = prev[query] ?? [];
            return settled.includes(provider) ? prev : { ...prev, [query]: [...settled, provider] };
          });
        }
      }
    },
    onAgentEvent: applyAgentMessage,
    onSlowStep: (agentType) => {
      const chapterKey = AGENT_CHAPTER_KEY[agentType];
      if (!chapterKey) return;
      setSlowChapters(prev => {
        if (prev.has(chapterKey)) return prev;
        const next = new Set(prev);
        next.add(chapterKey);
        return next;
      });
    },
    onAgentLifecycle: (agentType, status) => {
      const chapterKey = AGENT_CHAPTER_KEY[agentType];
      if (status === 'started') {
        if (chapterKey) markChapter(chapterKey, 'live');
        return;
      }
      completedAgentsRef.current.add(agentType);
      const bounds = progressBoundsFor(completedAgentsRef.current, rescan);
      setProgressBounds(bounds.floor, bounds.ceiling);
      if (chapterKey) trySettleChapter(chapterKey);
    },
  });

  // Armed once the run is over and re-armed for each pass, so a pass that never
  // reports its playback finished advances instead of holding the screen.
  useEffect(() => {
    if (!backendDone || minimized || finishedRef.current) return;
    const { advancePresentedChapter: advance } = presentationStepsRef.current;
    const timer = window.setTimeout(() => {
      const stuck = CHAPTERS[presentedChapterIndexRef.current];
      if (stuck) advance(stuck.key);
    }, PRESENTATION_PASS_MAX_MS);
    return () => clearTimeout(timer);
  }, [CHAPTERS, backendDone, presentedChapterIndex, minimized]);

  // A firm that already onboarded this website gets a screen, not an error
  // panel: the 409 names the company, and the company itself carries the date.
  const isDuplicate = failed === 'duplicate';
  const existingCompanyRecord = useExistingCompany(isDuplicate ? existingCompanyId : null);

  const getChapterState = (chapter: Chapter) => {
    if (done) return 'settled' as const;
    const index = CHAPTERS.findIndex(candidate => candidate.key === chapter.key);
    if (index < presentedChapterIndex) return 'settled' as const;
    if (index === presentedChapterIndex) return dwellChapterKey === chapter.key ? ('settled' as const) : ('live' as const);
    return 'queued' as const;
  };

  const getRailState = (chapter: Chapter) => getChapterState(chapter);

  const drawnPersonaCount = Math.min(revealedPersonaCount, personas.length);
  if (lastCardElapsedRef.current.drawn !== drawnPersonaCount) {
    lastCardElapsedRef.current = { drawn: drawnPersonaCount, at: chapterElapsedMs };
  }

  // How far the running stage has got, within its own segment. The site pass
  // has no denominator until the crawler has decided what to read, so it eases
  // towards a ceiling and the real ratio carries it the rest of the way.
  const liveFill = focalChapter.key === 'site'
    ? siteFill(chapterElapsedMs, siteScanData.totalPages, revealedPageCount)
    : focalChapter.key === 'customers'
      ? audienceFill(chapterElapsedMs, drawnPersonaCount, EXPECTED_PERSONA_COUNT, chapterElapsedMs - lastCardElapsedRef.current.at)
      : 0.5;

  if (minimized) return null;

  async function handleResume() {
    const sid = scanIdForResumeRef.current;
    if (!sid) { onError ? onError() : onDone(); return; }
    try {
      setResuming(true);
      await requestScanResume(sid);
      setFailed(null);
      chapterStatusRef.current = {};
      setChapterStatus({});
      chapterMessagesRef.current = {};
      setChapterMessages({});
      setSignals(0);
      completedAgentsRef.current = new Set();
      dwellTimersRef.current.forEach(clearTimeout);
      dwellTimersRef.current = [];
      pagesListReceivedRef.current = false;
      totalPagesRef.current = 0;
      pagesArrivedCountRef.current = 0;
      revealedPageCountRef.current = 0;
      setRevealedPageCount(0);
      revealedPersonaCountRef.current = 1;
      setRevealedPersonaCount(1);
      setPersonas([]);
      setPersonasQuiet(false);
      presentedChapterIndexRef.current = 0;
      setPresentedChapterIndex(0);
      presentationStartedRef.current = new Set();
      playbackDoneRef.current = new Set();
      setPlaybackDoneChapters({});
      chapterSeenRunningRef.current = new Set();
      backendDoneRef.current = false;
      setBackendDone(false);
      presentationCompleteRef.current = false;
      finishedRef.current = false;
      pendingCompletionRef.current = null;
      setReplayMessages(null);
      setDwellChapterKey(null);
      setDone(false);
      persistPresentationCheckpoint();
      setResumeKey(k => k + 1);
    } catch {
      onError ? onError() : onDone();
    } finally {
      setResuming(false);
    }
  }

  const fallback = failed && failed !== 'duplicate' ? FALLBACK_COPY[failed] : null;

  const content = (
    <div className="w-full">
      {!isDuplicate && (
        <div className="ob-scan-top mb-[clamp(24px,4vh,42px)]">
          <span
            className="ob-scan-eyebrow flex items-center gap-2 flex-wrap"
            style={{ fontSize: 13, letterSpacing: '-0.005em' }}
          >
            <span
              className="ob-scan-lane__dot shrink-0 rounded-full bg-primary"
              style={{ width: 6, height: 6 }}
            />
            <span>
              {failed === 'generic'
                ? _t('onboarding.crawl.eyebrowPaused')
                : _t('onboarding.crawl.eyebrowAnalyzing')}
            </span>
            <span className="text-muted-foreground font-medium">
              {displayName || _t('onboarding.crawl.defaultCompany')}
            </span>
            {!brandConfirmed && !done && !failed && <SpinnerDot size={14} />}
            <ScanEscape
              onStartOver={onStartOver && !rescan ? () => onStartOver() : undefined}
              onCancel={onCancel ? () => onCancel() : undefined}
              cancelCopy={rescan ? rescanCancelCopy(hasPrevious) : undefined}
            />
          </span>
          {signals > 0 && <SignalsOdometer value={signals} />}
        </div>
      )}

      {isDuplicate ? (
        <DuplicateWorkspacePanel
          companyName={existingCompany || _t('onboarding.crawl.defaultCompany')}
          websiteUrl={url}
          onboardedOn={existingCompanyRecord?.createdOn}
          onOpenWorkspace={() => onOpenExistingWorkspace?.(existingCompanyId)}
          onUseDifferentLink={() => onStartOver?.()}
        />
      ) : fallback ? (
        <div
          className="flex flex-col"
          data-testid="onboarding-scan-fallback"
          style={{ minHeight: 380, justifyContent: 'center', maxWidth: '46ch' }}
        >
          <div className="flex items-center gap-2 mb-3.5">
            <span
              className="relative shrink-0 rounded-full"
              style={{ width: 6, height: 6, backgroundColor: 'hsl(var(--status-published))' }}
            >
              <span
                className="absolute rounded-full animate-ping"
                style={{
                  inset: -4,
                  backgroundColor: 'hsl(var(--status-published))',
                  opacity: 0.35,
                }}
              />
            </span>
            <span className="text-badge-sm uppercase tracking-widest text-muted-foreground">
              {fallback.kicker}
            </span>
          </div>
          <h1 className="text-wizard-co text-foreground">{fallback.head}</h1>
          <p className="text-muted-foreground mt-4" style={{ fontSize: 16, lineHeight: 1.55 }}>
            {fallback.sub}
          </p>
          {fallback.eta && (
            <p className="text-muted-foreground mt-3" style={{ fontSize: 13 }}>
              {fallback.eta}
            </p>
          )}
          {fallback.canRetry && (
            <div className="mt-8">
              <button
                type="button"
                disabled={resuming}
                className="text-caption font-medium text-foreground hover:text-primary transition-colors disabled:opacity-50"
                onClick={handleResume}
              >
                {resuming
                  ? _t('onboarding.crawl.statusInProgress')
                  : _t('onboarding.crawl.btnTryAgain')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <ScanStageRail chapters={CHAPTERS} getState={getRailState} liveFill={liveFill} />

          <div style={{ minHeight: 360 }}>
            {done ? (
              <ScanReportCard url={url} />
            ) : (
              <ScanFocalChapter
                key={focalChapter.key}
                chapter={focalChapter}
                done={done}
                liveMessages={
                  replayMessages
                    ?? (focalChapter.key === 'customers'
                      ? [...(chapterMessages.customers ?? []), ...personaAgentLines]
                      : chapterMessages[focalChapter.key] ?? [])
                }
                replaying={replayMessages !== null}
                settled={dwellChapterKey === focalChapter.key}
                slowHint={
                  slowChapters.has(focalChapter.key) ? _t('onboarding.crawl.slowHint') : undefined
                }
                siteScanData={
                  focalChapter.key === 'site'
                    ? { ...siteScanData, pages: siteScanData.pages.slice(0, revealedPageCount) }
                    : undefined
                }
                googleQueries={focalChapter.key === 'google' ? googleQueries : undefined}
                serpSite={
                  focalChapter.key === 'google'
                    ? { host: hostOf(url), name: displayName, title: siteScanData.pages[0]?.title }
                    : undefined
                }
                googleComplete={chapterStatus.google === 'settled'}
                onGooglePlaybackComplete={() => markPlaybackDone('google')}
                sitePagesFinal={chapterStatus.site === 'settled' && revealedPageCount >= siteScanData.pages.length}
                onSitePlaybackComplete={() => markPlaybackDone('site')}
                assistantsComplete={chapterStatus.assistants === 'settled'}
                onAssistantsPlaybackComplete={() => markPlaybackDone('assistants')}
                assistantsData={
                  focalChapter.key === 'assistants'
                    ? {
                        quote: assistantQueries[assistantQueries.length - 1] ?? description,
                        queries: assistantQueries,
                        activeProvider: assistantActiveProvider,
                        providerCounts: assistantProviderCounts,
                        providerResults: assistantProviderResults,
                      }
                    : undefined
                }
                customersData={
                  focalChapter.key === 'customers'
                    ? {
                        personas: personas.slice(0, revealedPersonaCount),
                        expectedTotal: EXPECTED_PERSONA_COUNT,
                        progressKey: scanIdForResumeRef.current ?? initialScanId ?? undefined,
                        audienceFacts,
                      }
                    : undefined
                }
              />
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div
      className="th ob-scan fixed inset-0 z-[60] overflow-y-auto flex flex-col items-center"
      style={{ background: 'var(--onboarding-ambient)' }}
    >
      <AmbientGlow level={isDuplicate ? 'rest' : 'active'} />
      <Topbar
        quiet={!isDuplicate}
        showWalkthrough={!rescan}
        primaryAction={
          failed || !scanReady || pct < BACKGROUND_BUTTON_MIN_PCT
            ? undefined
            : {
                label: _t('onboarding.crawl.btnBackground'),
                onClick: () => {
                  if (onRunInBackground) {
                    persistPresentationCheckpoint();
                    onRunInBackground(pctRef.current);
                  } else {
                    onDone();
                  }
                },
              }
        }
      />
      <div className={cn('w-full max-w-[880px] pb-10 px-10', isDuplicate ? 'pt-0' : 'pt-[clamp(24px,7vh,64px)]')}>{content}</div>
    </div>
  );
}
